// Face-tracked effects (v0.7, "filter bám mặt" like TikTok): AR stickers anchored to the
// Face Landmarker mesh + makeup regions. DOM-free: the camera worker draws them into the
// outgoing frame (so the partner, the preview and the stored photo all have them), the
// tests check the geometry, and the booth UI renders the sticker previews.
//
// All geometry is in pixels of the image the effect is drawn into; landmarks are
// normalised (x, y) pairs (478 points) of the same frame, un-mirrored.

export type StickerId = 'none' | 'cat' | 'bunny' | 'bear' | 'heartglasses' | 'crown' | 'flowers' | 'blush' | 'devil';

export interface StickerDef {
  id: StickerId;
  name: string;
  /** atlas cell (ATLAS_COLS × ATLAS_ROWS grid of CELL px) */
  cell: number;
  /** what the sprite hangs on */
  anchor: 'top' | 'eyes' | 'cheeks';
  /** sprite size in IPD units (width, height) */
  size: [number, number];
  /** offset of the sprite centre in head space (IPD units; x → subject's image right, y → up) */
  offset: [number, number];
}

export const CELL = 256;
export const ATLAS_COLS = 4;
export const ATLAS_ROWS = 2;

export const STICKERS: StickerDef[] = [
  { id: 'cat', name: 'Tai mèo', cell: 0, anchor: 'top', size: [5.2, 2.6], offset: [0, 1.15] },
  { id: 'bunny', name: 'Tai thỏ', cell: 1, anchor: 'top', size: [4.2, 4.2], offset: [0, 2.0] },
  { id: 'bear', name: 'Gấu', cell: 2, anchor: 'top', size: [5.2, 2.6], offset: [0, 0.85] },
  { id: 'heartglasses', name: 'Kính tim', cell: 3, anchor: 'eyes', size: [3.3, 1.65], offset: [0, 0.02] },
  { id: 'crown', name: 'Vương miện', cell: 4, anchor: 'top', size: [3.0, 1.9], offset: [0, 1.3] },
  { id: 'flowers', name: 'Vòng hoa', cell: 5, anchor: 'top', size: [5.0, 1.9], offset: [0, 0.6] },
  { id: 'blush', name: 'Má ửng', cell: 6, anchor: 'cheeks', size: [3.9, 1.4], offset: [0, -0.9] },
  { id: 'devil', name: 'Sừng quỷ', cell: 7, anchor: 'top', size: [4.2, 1.8], offset: [0, 1.05] },
];

export const stickerById = (id: string) => STICKERS.find((s) => s.id === id) ?? null;

/** Face Landmarker indices used by the effects. */
export const FM = {
  irisA: 468, irisB: 473, forehead: 10, chin: 152, nose: 1, cheekL: 234, cheekR: 454,
  /** cheek apples (blush) */
  appleA: 50, appleB: 280,
  /** outer and inner lip contours, in order (lipstick polygon = outer minus inner) */
  lipsOuter: [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146],
  lipsInner: [78, 191, 80, 81, 82, 13, 312, 311, 310, 415, 308, 324, 318, 402, 317, 14, 87, 178, 88, 95],
};
export const FM_MIN = 478;

/** Head frame in image pixels. */
export interface HeadFrame {
  /** midpoint between the irises */
  mid: [number, number];
  /** unit vector along the eyes (towards the eye with the larger image x when upright) */
  ex: [number, number];
  /** unit "up" vector of the head (image y grows downwards) */
  up: [number, number];
  /** inter-pupil distance, px */
  ipd: number;
  /** head yaw −1..1 (nose offset against half the face width) */
  yaw: number;
  /** top of the forehead (landmark 10), px */
  top: [number, number];
}

export function headFrame(lm: ArrayLike<number>, w: number, h: number): HeadFrame | null {
  if (!lm || lm.length < FM_MIN * 2) return null;
  const P = (i: number): [number, number] => [lm[i * 2] * w, lm[i * 2 + 1] * h];
  let a = P(FM.irisA), b = P(FM.irisB);
  if (a[0] > b[0]) [a, b] = [b, a];
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const ipd = Math.hypot(dx, dy);
  if (!(ipd > 2)) return null;
  const ex: [number, number] = [dx / ipd, dy / ipd];
  const up: [number, number] = [ex[1], -ex[0]];
  const mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const nose = P(FM.nose);
  const faceW = Math.hypot(P(FM.cheekR)[0] - P(FM.cheekL)[0], P(FM.cheekR)[1] - P(FM.cheekL)[1]) || ipd * 2.3;
  const along = (nose[0] - mid[0]) * ex[0] + (nose[1] - mid[1]) * ex[1];
  const yaw = Math.max(-1, Math.min(1, along / (faceW * 0.5)));
  return { mid, ex, up, ipd, yaw, top: P(FM.forehead) };
}

/** Sprite quad: corners (top-left, top-right, bottom-right, bottom-left) in px + atlas UV rect. */
export interface StickerQuad {
  corners: [number, number][];
  uv: [number, number, number, number];
}

export function stickerQuad(def: StickerDef, f: HeadFrame): StickerQuad {
  const base: [number, number] = def.anchor === 'top' ? f.top : f.mid;
  // turning the head: the sprite narrows and slides towards the side the face turns to
  const squash = 1 - 0.35 * Math.abs(f.yaw);
  const w = def.size[0] * f.ipd * squash, hgt = def.size[1] * f.ipd;
  const ox = def.offset[0] * f.ipd + f.yaw * 0.35 * f.ipd * (def.anchor === 'top' ? 1 : 0.6);
  const oy = def.offset[1] * f.ipd;
  const c: [number, number] = [base[0] + f.ex[0] * ox + f.up[0] * oy, base[1] + f.ex[1] * ox + f.up[1] * oy];
  const hx: [number, number] = [f.ex[0] * w / 2, f.ex[1] * w / 2];
  const hy: [number, number] = [f.up[0] * hgt / 2, f.up[1] * hgt / 2];
  const corners: [number, number][] = [
    [c[0] - hx[0] + hy[0], c[1] - hx[1] + hy[1]],
    [c[0] + hx[0] + hy[0], c[1] + hx[1] + hy[1]],
    [c[0] + hx[0] - hy[0], c[1] + hx[1] - hy[1]],
    [c[0] - hx[0] - hy[0], c[1] - hx[1] - hy[1]],
  ];
  const col = def.cell % ATLAS_COLS, row = Math.floor(def.cell / ATLAS_COLS);
  return { corners, uv: [col / ATLAS_COLS, row / ATLAS_ROWS, (col + 1) / ATLAS_COLS, (row + 1) / ATLAS_ROWS] };
}

/** Lip polygons in px (outer ring, inner ring). */
export function lipPolygons(lm: ArrayLike<number>, w: number, h: number): { outer: [number, number][]; inner: [number, number][] } | null {
  if (!lm || lm.length < FM_MIN * 2) return null;
  const P = (i: number): [number, number] => [lm[i * 2] * w, lm[i * 2 + 1] * h];
  return { outer: FM.lipsOuter.map(P), inner: FM.lipsInner.map(P) };
}

/** Triangle fan of a closed ring around its centroid → flat [x, y, ...] list (px). */
export function fan(ring: [number, number][]): number[] {
  const cx = ring.reduce((s, p) => s + p[0], 0) / ring.length, cy = ring.reduce((s, p) => s + p[1], 0) / ring.length;
  const out: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    out.push(cx, cy, a[0], a[1], b[0], b[1]);
  }
  return out;
}

/** Blush spots: centre px + radius px for both cheek apples. */
export function blushSpots(lm: ArrayLike<number>, w: number, h: number, f: HeadFrame): [number, number, number][] {
  const P = (i: number): [number, number] => [lm[i * 2] * w, lm[i * 2 + 1] * h];
  return [FM.appleA, FM.appleB].map((i) => { const p = P(i); return [p[0], p[1], f.ipd * 0.42] as [number, number, number]; });
}

// ------------------------------------------------------------------ atlas (procedural art)

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Draws every sticker into its atlas cell. Works on a DOM or an Offscreen 2D context. */
export function drawAtlas(ctx: Ctx) {
  ctx.clearRect(0, 0, CELL * ATLAS_COLS, CELL * ATLAS_ROWS);
  for (const s of STICKERS) {
    const x0 = (s.cell % ATLAS_COLS) * CELL, y0 = Math.floor(s.cell / ATLAS_COLS) * CELL;
    ctx.save();
    ctx.translate(x0, y0);
    ctx.beginPath();
    ctx.rect(0, 0, CELL, CELL);
    ctx.clip();
    ART[s.id]?.(ctx, CELL);
    ctx.restore();
  }
}

const ear = (ctx: Ctx, x: number, y: number, w: number, h: number, tilt: number, outer: string, inner: string | null) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(tilt);
  ctx.beginPath();
  ctx.moveTo(-w / 2, 0);
  ctx.quadraticCurveTo(-w * 0.35, -h * 0.7, 0, -h);
  ctx.quadraticCurveTo(w * 0.35, -h * 0.7, w / 2, 0);
  ctx.closePath();
  ctx.fillStyle = outer;
  ctx.fill();
  if (inner) {
    ctx.beginPath();
    ctx.moveTo(-w * 0.28, -h * 0.08);
    ctx.quadraticCurveTo(-w * 0.18, -h * 0.6, 0, -h * 0.78);
    ctx.quadraticCurveTo(w * 0.18, -h * 0.6, w * 0.28, -h * 0.08);
    ctx.closePath();
    ctx.fillStyle = inner;
    ctx.fill();
  }
  ctx.restore();
};

const heart = (ctx: Ctx, x: number, y: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x, y + r * 0.9);
  ctx.bezierCurveTo(x - r * 1.6, y - r * 0.2, x - r * 0.7, y - r * 1.3, x, y - r * 0.45);
  ctx.bezierCurveTo(x + r * 0.7, y - r * 1.3, x + r * 1.6, y - r * 0.2, x, y + r * 0.9);
  ctx.closePath();
};

const ART: Partial<Record<StickerId, (ctx: Ctx, S: number) => void>> = {
  cat(ctx, S) {
    // light fur so the ears read against dark hair
    ear(ctx, S * 0.2, S * 0.92, S * 0.3, S * 0.72, -0.35, '#F4E3D7', '#FF9EB5');
    ear(ctx, S * 0.8, S * 0.92, S * 0.3, S * 0.72, 0.35, '#F4E3D7', '#FF9EB5');
  },
  bunny(ctx, S) {
    ear(ctx, S * 0.36, S * 0.98, S * 0.2, S * 0.9, -0.12, '#FFFFFF', '#FFC2D1');
    ear(ctx, S * 0.64, S * 0.98, S * 0.2, S * 0.9, 0.12, '#FFFFFF', '#FFC2D1');
  },
  bear(ctx, S) {
    for (const x of [0.17, 0.83]) {
      ctx.beginPath(); ctx.arc(S * x, S * 0.62, S * 0.16, 0, Math.PI * 2); ctx.fillStyle = '#8A5A3C'; ctx.fill();
      ctx.beginPath(); ctx.arc(S * x, S * 0.62, S * 0.09, 0, Math.PI * 2); ctx.fillStyle = '#E8B48F'; ctx.fill();
    }
  },
  heartglasses(ctx, S) {
    ctx.fillStyle = '#FF3D7F';
    heart(ctx, S * 0.29, S * 0.52, S * 0.17); ctx.fill();
    heart(ctx, S * 0.71, S * 0.52, S * 0.17); ctx.fill();
    ctx.globalAlpha = 0.35; ctx.fillStyle = '#FFFFFF';
    ctx.beginPath(); ctx.ellipse(S * 0.24, S * 0.42, S * 0.04, S * 0.025, -0.6, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(S * 0.66, S * 0.42, S * 0.04, S * 0.025, -0.6, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#FF3D7F'; ctx.lineWidth = S * 0.025;
    ctx.beginPath(); ctx.moveTo(S * 0.44, S * 0.46); ctx.quadraticCurveTo(S * 0.5, S * 0.42, S * 0.56, S * 0.46); ctx.stroke();
  },
  crown(ctx, S) {
    ctx.beginPath();
    ctx.moveTo(S * 0.12, S * 0.85);
    ctx.lineTo(S * 0.12, S * 0.35); ctx.lineTo(S * 0.31, S * 0.58); ctx.lineTo(S * 0.5, S * 0.18);
    ctx.lineTo(S * 0.69, S * 0.58); ctx.lineTo(S * 0.88, S * 0.35); ctx.lineTo(S * 0.88, S * 0.85);
    ctx.closePath();
    const g = ctx.createLinearGradient(0, S * 0.2, 0, S * 0.85);
    g.addColorStop(0, '#FFE58A'); g.addColorStop(1, '#E9A93A');
    ctx.fillStyle = g; ctx.fill();
    for (const [x, y, c] of [[0.5, 0.62, '#FF4F7B'], [0.28, 0.72, '#4FC3FF'], [0.72, 0.72, '#4FC3FF']] as const) {
      ctx.beginPath(); ctx.arc(S * x, S * y, S * 0.05, 0, Math.PI * 2); ctx.fillStyle = c; ctx.fill();
    }
  },
  flowers(ctx, S) {
    const cols = ['#FF8FB1', '#FFD166', '#FFFFFF', '#C3A6FF', '#FF8FB1', '#FFD166', '#FFFFFF'];
    cols.forEach((c, i) => {
      const x = S * (0.1 + i * 0.133), y = S * (0.62 - Math.sin((i / (cols.length - 1)) * Math.PI) * 0.16);
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * Math.PI * 2;
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * S * 0.035, y + Math.sin(a) * S * 0.035, S * 0.04, 0, Math.PI * 2);
        ctx.fillStyle = c; ctx.fill();
      }
      ctx.beginPath(); ctx.arc(x, y, S * 0.025, 0, Math.PI * 2); ctx.fillStyle = '#F4A300'; ctx.fill();
    });
  },
  blush(ctx, S) {
    for (const x of [0.2, 0.8]) {
      const g = ctx.createRadialGradient(S * x, S * 0.55, 0, S * x, S * 0.55, S * 0.16);
      g.addColorStop(0, 'rgba(255,110,150,0.75)'); g.addColorStop(1, 'rgba(255,110,150,0)');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(S * x, S * 0.55, S * 0.16, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(255,90,130,0.85)'; ctx.lineWidth = S * 0.012;
      for (let k = -1; k <= 1; k++) {
        ctx.beginPath(); ctx.moveTo(S * (x - 0.05 + k * 0.045), S * 0.6); ctx.lineTo(S * (x - 0.02 + k * 0.045), S * 0.5); ctx.stroke();
      }
    }
    ctx.fillStyle = '#FFFFFF';
    for (const [x, y] of [[0.06, 0.3], [0.94, 0.32]]) {
      ctx.beginPath(); ctx.moveTo(S * x, S * (y - 0.06)); ctx.lineTo(S * (x + 0.015), S * (y - 0.015)); ctx.lineTo(S * (x + 0.06), S * y);
      ctx.lineTo(S * (x + 0.015), S * (y + 0.015)); ctx.lineTo(S * x, S * (y + 0.06)); ctx.lineTo(S * (x - 0.015), S * (y + 0.015));
      ctx.lineTo(S * (x - 0.06), S * y); ctx.lineTo(S * (x - 0.015), S * (y - 0.015)); ctx.closePath(); ctx.fill();
    }
  },
  devil(ctx, S) {
    for (const [x, d] of [[0.24, -1], [0.76, 1]] as const) {
      ctx.beginPath();
      ctx.moveTo(S * (x - 0.09), S * 0.9);
      ctx.quadraticCurveTo(S * (x - 0.05), S * 0.45, S * (x + d * 0.12), S * 0.12);
      ctx.quadraticCurveTo(S * (x + d * 0.02), S * 0.5, S * (x + 0.09), S * 0.9);
      ctx.closePath();
      ctx.fillStyle = '#E8283C'; ctx.fill();
    }
  },
};
